/**
 * CAP-COMPLETE — capability matrix helpers (§3.7.3).
 * Soft≠Live: frontend consumes backend posture; no role/caps.length guesses.
 * Operator copy stays path-free; receipts show object/operation id only.
 */

import { CAP_HOME } from "./cmc-entry-registry.js";

/** Exact SA capability strings (mirror api/sa_capabilities.py). */
export const EXACT_SA_CAPS = Object.freeze([
  "sa_full_access",
  "sa_billing_admin",
  "sa_device_admin",
  "sa_ota_admin",
  "sa_audit_read",
  "sa_security_admin",
  "sa_user_admin",
  "sa_ai_admin",
]);

const EXACT = new Set(EXACT_SA_CAPS);

/** True when principal holds an exact SA seat (never caps.length alone). */
export function hasExplicitSaSeat(me) {
  if (!me) return false;
  const role = String(me.role || "");
  if (role === "superadmin") return true;
  if (role !== "sa_staff") return false;
  const caps = Array.isArray(me.caps) ? me.caps : [];
  return caps.some((c) => EXACT.has(String(c || "").trim()));
}

/** Tenant Admin seat for scoped Machine Cap — never sa_staff via caps.length. */
export function hasTenantAdminSeat(me) {
  if (!me) return false;
  const role = String(me.role || "");
  return role === "admin" || role === "superadmin";
}

/** Hidden lab (Fleet / Edge / Control) — SA exact seat only. Tenant admin is not Platform. */
export function canOpenHiddenLab(me) {
  return hasExplicitSaSeat(me);
}

/** Activate / claim — tenant admin, SA device seat, or an explicit claim bit. Never paint the door otherwise. */
export function canOpenActivateSeat(me) {
  if (!me) return false;
  const role = String(me.role || "");
  if (role === "superadmin" || role === "admin") return true;
  if (role === "sa_staff") {
    const caps = Array.isArray(me.caps) ? me.caps : [];
    return caps.includes("sa_full_access") || caps.includes("sa_device_admin");
  }
  const pol = me.policy && typeof me.policy === "object" ? me.policy : {};
  return Number(pol.can_claim_device) === 1;
}

/** Omit from the DOM when the seat cannot use the control. Never paint disabled theatre. */
export function onlyIf(ok, node) {
  return ok ? node : null;
}

/**
 * Pull a short operator receipt from a Live write body.
 * Prefers operation_id / campaign_id / id — never paints HTTP paths.
 */
export function actionReceiptOf(body, fallbackLabel) {
  const b = body && typeof body === "object" ? body : {};
  const oid =
    b.operation_id
    || b.operationId
    || (b.operation && (b.operation.id || b.operation.operation_id))
    || b.campaign_id
    || (b.campaign && b.campaign.campaign_id)
    || b.id
    || b.receipt_id
    || null;
  const observed =
    b.observed_state
    || b.state
    || (b.campaign && b.campaign.state)
    || (b.physical_observed === true ? "observed" : null)
    || (b.device_ack_observed === true ? "acked" : null)
    || null;
  const label = String(fallbackLabel || "Done").trim() || "Done";
  if (oid && observed) return `${label} · ${oid} · ${observed}`;
  if (oid) return `${label} · ${oid}`;
  if (observed) return `${label} · ${observed}`;
  return label;
}

/** 403/409 (and friends) must never be toasted as success. */
export function isWriteDeniedStatus(err) {
  const n = String(err == null ? "" : err);
  return /\b403\b|\b401\b|\b409\b|\b429\b|\bforbidden\b|\bconflict\b/i.test(n);
}

/** Toast helper: go only on real success body; warn/bad on deny. */
export function toastWriteResult(toast, err, body, label) {
  if (!toast) return;
  if (err || body == null) {
    const msg = String(err || "That write did not complete.");
    toast(msg.length > 160 ? msg.slice(0, 157) + "…" : msg, isWriteDeniedStatus(err) ? "warn" : "bad");
    return;
  }
  toast(actionReceiptOf(body, label), "go");
}

/** §3.7.3 inventory rows — one Configure home each (CAP_HOME ids). */
export const CAP_MATRIX = Object.freeze([
  { id: "account_access", configure: "group_grants", operate: null, evidence: "staff_permissions" },
  { id: "notifications", configure: null, path: "/settings/notify", operate: null, evidence: null },
  { id: "alarm_trigger", configure: "trigger_policy", operate: null, evidence: null },
  { id: "models", configure: null, path: "/settings/models", operate: null, evidence: null },
  { id: "ai_policy", configure: "ai_policy", operate: null, evidence: null },
  { id: "cameras", configure: null, path: "/settings/cameras", operate: null, evidence: null },
  { id: "sites", configure: "sites", operate: null, evidence: null },
  { id: "areas", configure: "areas", operate: "areas", evidence: null },
  { id: "machine_identity", configure: null, path: "/machine", operate: null, evidence: "machine_health" },
  { id: "machine_cap_packs", configure: "machine_capability_packs", operate: "automation", evidence: null },
  { id: "automations", configure: "automation", operate: "needs_you", evidence: null },
  { id: "ota_cap", configure: "ota_capability_rollout", operate: "machine_capability_packs", evidence: null },
  { id: "ota_base", configure: null, path: "/ota/base", operate: null, evidence: null },
  { id: "staff_permissions", configure: "staff_permissions", operate: null, evidence: null },
  { id: "edge_lab", configure: "edge_lab", operate: null, evidence: null },
]);

export function configureHomeOf(matrixId) {
  const row = CAP_MATRIX.find((r) => r.id === matrixId);
  if (!row) return null;
  if (row.configure && CAP_HOME[row.configure]) return CAP_HOME[row.configure];
  if (row.path) return { id: matrixId, label: matrixId, path: row.path, primary: "settings" };
  return null;
}

export default {
  EXACT_SA_CAPS,
  hasExplicitSaSeat,
  hasTenantAdminSeat,
  canOpenHiddenLab,
  canOpenActivateSeat,
  onlyIf,
  actionReceiptOf,
  isWriteDeniedStatus,
  toastWriteResult,
  CAP_MATRIX,
  configureHomeOf,
};
